/**
 * GIWAHS — Transactional Email Templates
 * ---------------------------------------------------------------------
 * Single source of truth for every transactional email the platform
 * sends. Each template exports a function `(payload) => { subject, html,
 * text }`. HTML uses inline styles + table layout for compatibility with
 * email clients (Gmail mobile, Outlook desktop, Apple Mail). Plain-text
 * is a clean monospace-friendly rendering.
 *
 * Branding posture: premium, sharp-edged, intelligence-focused, NO
 * marketing fluff, NO emojis, NO promotional CTAs beyond "View" links.
 *
 * Templates live in the frontend codebase so we can preview them in
 * Storybook later if needed; the Edge Function imports the same shape
 * via the shared types defined at the top.
 */

const BRAND       = 'GIWAHS';
const BRAND_LINE  = 'Sourcing intelligence — verified factories, editorial trends, transparent supply.';
const PLATFORM    = 'https://giwahs.com';
const BRAND_LOGO_URL = `${PLATFORM}/brand/giwahs-logo.png`;
const SUPPORT_EMAIL = 'hello@giwahs.com';
const COLOR_BG    = '#FFFFFF';
const COLOR_FG    = '#0A0A0A';
const COLOR_MUTED = '#525252';
const COLOR_RULE  = '#E5E5E5';
const COLOR_PANEL = '#F5F5F0';

// ─── Shared shell helpers ─────────────────────────────────────────────
const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
}[c]));

/** Outer HTML wrapper — same shell for every email. */
const shell = ({ preheader = '', body }) => `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<meta name="x-apple-disable-message-reformatting">
<title>${escapeHtml(BRAND)}</title>
</head>
<body style="margin:0;padding:0;background:${COLOR_PANEL};color:${COLOR_FG};font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;-webkit-font-smoothing:antialiased;">
<div style="display:none;font-size:1px;line-height:1px;max-height:0;max-width:0;opacity:0;overflow:hidden;">${escapeHtml(preheader)}</div>
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background:${COLOR_PANEL};">
  <tr><td align="center" style="padding:32px 16px;">
    <table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" style="max-width:600px;background:${COLOR_BG};border:1px solid ${COLOR_FG};">
      <tr><td style="padding:24px 32px;border-bottom:1px solid ${COLOR_FG};background:#000000;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
          <tr>
            <td><a href="${PLATFORM}" style="text-decoration:none;"><img src="${BRAND_LOGO_URL}" alt="${escapeHtml(BRAND)} — trading as Whaitiri Black Limited" height="56" style="height:56px;width:auto;display:block;border:0;outline:none;text-decoration:none;"></a></td>
            <td align="right" style="font-family:Menlo,Consolas,'SF Mono',monospace;font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:#A3A3A3;">Sourcing Intelligence</td>
          </tr>
        </table>
      </td></tr>
      ${body}
      <tr><td style="padding:24px 32px;border-top:1px solid ${COLOR_FG};background:${COLOR_PANEL};">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
          <tr><td style="font-family:Menlo,Consolas,'SF Mono',monospace;font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:${COLOR_MUTED};">${BRAND} · ${BRAND_LINE}</td></tr>
          <tr><td style="padding-top:8px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:11px;color:${COLOR_MUTED};">This is an operational message from GIWAHS. You're receiving it because of an action taken on the platform. <a href="${PLATFORM}/privacy" style="color:${COLOR_FG};text-decoration:underline;">Privacy</a> · <a href="${PLATFORM}/contact" style="color:${COLOR_FG};text-decoration:underline;">Contact</a></td></tr>
        </table>
      </td></tr>
    </table>
  </td></tr>
</table>
</body></html>`;

/** Compact section helper. */
const overline = (label) => `<tr><td style="padding:0 32px 8px 32px;font-family:Menlo,Consolas,'SF Mono',monospace;font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:${COLOR_MUTED};">/ ${escapeHtml(label)}</td></tr>`;

const display = (title) => `<tr><td style="padding:8px 32px 24px 32px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:32px;font-weight:700;line-height:1.05;letter-spacing:-0.025em;color:${COLOR_FG};">${escapeHtml(title)}</td></tr>`;

const para = (text) => `<tr><td style="padding:0 32px 16px 32px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:14px;line-height:1.5;color:${COLOR_FG};">${escapeHtml(text)}</td></tr>`;

/** Key-value reference block (sharp-edged panel). */
const kvBlock = (rows) => {
  const tr = rows.map(([k, v]) => `
    <tr>
      <td style="padding:12px 16px;border-bottom:1px solid ${COLOR_RULE};font-family:Menlo,Consolas,'SF Mono',monospace;font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:${COLOR_MUTED};vertical-align:top;width:40%;">${escapeHtml(k)}</td>
      <td style="padding:12px 16px;border-bottom:1px solid ${COLOR_RULE};font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:13px;color:${COLOR_FG};vertical-align:top;">${escapeHtml(v)}</td>
    </tr>`).join('');
  return `<tr><td style="padding:0 32px 24px 32px;">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border:1px solid ${COLOR_FG};background:${COLOR_BG};">${tr}</table>
  </td></tr>`;
};

const cta = (href, label) => `<tr><td style="padding:0 32px 32px 32px;">
  <table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr>
    <td style="background:${COLOR_FG};border:1px solid ${COLOR_FG};">
      <a href="${escapeHtml(href)}" style="display:inline-block;padding:14px 20px;font-family:Menlo,Consolas,'SF Mono',monospace;font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:${COLOR_BG};text-decoration:none;">${escapeHtml(label)} →</a>
    </td>
  </tr></table>
</td></tr>`;

/** Status pill — matches the header badge style used on /track. */
const statusPill = (status) => `<tr><td style="padding:0 32px 16px 32px;">
  <span style="display:inline-block;border:1px solid ${COLOR_FG};background:${COLOR_FG};color:${COLOR_BG};padding:4px 10px;font-family:Menlo,Consolas,'SF Mono',monospace;font-size:10px;letter-spacing:0.12em;text-transform:uppercase;">● ${escapeHtml(status)}</span>
</td></tr>`;

/** QR + tracking-link card. Renders the QR as an inline <img src="data:...">
 *  (works in Apple Mail, Outlook desktop, ProtonMail, FastMail; Gmail web
 *  may strip data URLs — the prominent text CTA below is the universal
 *  fallback). */
const qrCard = ({ tracking_url, qr_data_url, reference_code }) => `<tr><td style="padding:0 32px 24px 32px;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="border:1px solid ${COLOR_FG};background:${COLOR_PANEL};">
    <tr>
      <td style="padding:20px;width:170px;vertical-align:top;" align="center">
        ${qr_data_url ? `<img src="${escapeHtml(qr_data_url)}" alt="QR for ${escapeHtml(reference_code || '')}" width="150" height="150" style="display:block;border:1px solid ${COLOR_FG};background:${COLOR_BG};image-rendering:pixelated;">` : ''}
      </td>
      <td style="padding:20px 20px 20px 0;vertical-align:top;">
        <div style="font-family:Menlo,Consolas,'SF Mono',monospace;font-size:10px;letter-spacing:0.12em;text-transform:uppercase;color:${COLOR_MUTED};">Track this RFQ</div>
        <div style="margin-top:6px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:18px;font-weight:700;color:${COLOR_FG};">Scan, or open the link below.</div>
        <div style="margin-top:10px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:13px;line-height:1.5;color:${COLOR_FG};">No account needed — your reference code is the key.</div>
        <div style="margin-top:12px;">
          <a href="${escapeHtml(tracking_url)}" style="display:inline-block;background:${COLOR_FG};color:${COLOR_BG};padding:10px 14px;font-family:Menlo,Consolas,'SF Mono',monospace;font-size:10px;letter-spacing:0.12em;text-transform:uppercase;text-decoration:none;">Open tracking link →</a>
        </div>
        <div style="margin-top:10px;font-family:Menlo,Consolas,'SF Mono',monospace;font-size:11px;color:${COLOR_MUTED};word-break:break-all;">${escapeHtml(tracking_url)}</div>
      </td>
    </tr>
  </table>
</td></tr>`;

// ─── Plain-text helpers ───────────────────────────────────────────────
const txtRule = '----------------------------------------------------------------';
const txtHeader = (subject) => `${BRAND}.\n${BRAND_LINE}\n${txtRule}\n${subject}\n${txtRule}\n`;
const txtFooter = `\n${txtRule}\n${BRAND} · ${BRAND_LINE}\nPrivacy: ${PLATFORM}/privacy  ·  Contact: ${PLATFORM}/contact\nThis is an operational message — you're receiving it because of an action taken on the platform.\n`;
const txtKV = (rows) => rows.map(([k, v]) => `  ${k.padEnd(22, ' ')}  ${v ?? ''}`).join('\n');

// ─── Templates ────────────────────────────────────────────────────────

/**
 * 1. RFQ submitted — buyer acknowledgement
 */
export const rfqConfirmation = (p = {}) => {
  const subject = `Your RFQ is in queue — ${p.reference_code || 'reference pending'}`;
  const tracking_url = p.tracking_url || `${PLATFORM}/track?ref=${encodeURIComponent(p.reference_code || '')}`;
  const status = p.status_label || 'Submitted';
  const eta = p.response_eta || 'within two business days';
  const supportEmail = p.support_email || SUPPORT_EMAIL;
  const rows = [
    ['Reference',       p.reference_code || '—'],
    ['Status',          status],
    ['Product',         p.product_name || p.category || '—'],
    ['Quantity',        p.quantity ? `${p.quantity} units` : '—'],
    ['Destination',     p.destination_country || '—'],
    ['Target price',    p.target_price || '—'],
    ['Timeline',        p.timeline || '—'],
    ['Expected reply',  eta],
  ];
  const html = shell({
    preheader: `Your sourcing request is logged. Reference ${p.reference_code || 'pending'}.`,
    body:
      overline('Request for quotation') +
      display('Your RFQ is in queue.') +
      statusPill(status) +
      para(`Thanks, ${p.contact_name || 'team'}. We've logged your request and the GIWAHS sourcing desk is matching it against verified factories. Expect a curated shortlist with capacity, lead-time and indicative pricing ${eta}.`) +
      qrCard({ tracking_url, qr_data_url: p.qr_data_url, reference_code: p.reference_code }) +
      kvBlock(rows) +
      para(`Questions? Reply to this email or write to ${supportEmail}. Our sourcing desk is one message away — typical response within one business day.`) +
      cta(`${PLATFORM}/rfq`, 'Submit another request'),
  });
  const text = `${txtHeader(subject)}
Thanks, ${p.contact_name || 'team'}.

Your RFQ is in queue. Status: ${status}.

We've logged your request and the GIWAHS sourcing desk is matching it
against verified factories. Expect a curated shortlist with capacity,
lead-time and indicative pricing ${eta}.

Track this RFQ anytime — no account needed:
${tracking_url}

${txtKV(rows)}

Questions?  Reply to this email or write to ${supportEmail}.
Submit another request: ${PLATFORM}/rfq
${txtFooter}`;
  return { subject, html, text };
};

/**
 * 2. RFQ submitted — internal admin notification
 */
export const rfqAdminAlert = (p = {}) => {
  const subject = `[RFQ] ${p.reference_code || 'NEW'} · ${p.category || 'uncategorised'} · ${p.quantity || '?'} units · ${p.destination_country || 'unknown'}`;
  const rows = [
    ['Reference',       p.reference_code || '—'],
    ['Contact',         p.contact_name || '—'],
    ['Company',         p.company || '—'],
    ['Email',           p.contact_email || '—'],
    ['Phone',           p.contact_phone || '—'],
    ['Category',        p.category || '—'],
    ['Product',         p.product_name || '—'],
    ['Quantity',        p.quantity ? `${p.quantity} units` : '—'],
    ['Materials',       p.materials || '—'],
    ['Branding',        p.branding || '—'],
    ['Packaging',       p.packaging || '—'],
    ['Destination',     p.destination_country || '—'],
    ['Target price',    p.target_price || '—'],
    ['Timeline',        p.timeline || '—'],
    ['Notes',           p.notes || '—'],
  ];
  const html = shell({
    preheader: `Internal: new RFQ from ${p.contact_email || 'unknown'}.`,
    body:
      overline('Internal alert · new RFQ') +
      display(p.reference_code || 'New RFQ') +
      kvBlock(rows) +
      cta(`${PLATFORM}/admin/rfqs/${encodeURIComponent(p.reference_code || '')}`, 'Open in admin'),
  });
  const text = `${txtHeader(subject)}
${txtKV(rows)}
${txtFooter}`;
  return { subject, html, text };
};

/**
 * 3. Contact form — submitter acknowledgement
 */
export const contactAck = (p = {}) => {
  const subject = `Message received — ${p.topic || 'general'}`;
  const html = shell({
    preheader: `Thanks for reaching out — we'll respond within 24 hours.`,
    body:
      overline('Contact · acknowledgement') +
      display('Message received.') +
      para(`Thanks${p.name ? ', ' + p.name : ''}. Your message has reached the GIWAHS team. We respond to all platform contacts within one business day — typically faster.`) +
      kvBlock([
        ['Topic',   p.topic   || 'General'],
        ['Company', p.company || '—'],
      ]) +
      para(`If your enquiry is time-sensitive sourcing, the fastest path is the RFQ form — the sourcing desk picks those up first.`) +
      cta(`${PLATFORM}/rfq`, 'Start an RFQ instead'),
  });
  const text = `${txtHeader(subject)}
Thanks${p.name ? ', ' + p.name : ''}.

Your message has reached the GIWAHS team. We respond to all platform
contacts within one business day — typically faster.

  Topic:    ${p.topic || 'General'}
  Company:  ${p.company || '—'}

If your enquiry is time-sensitive sourcing, the fastest path is the
RFQ form at ${PLATFORM}/rfq — the sourcing desk picks those up first.
${txtFooter}`;
  return { subject, html, text };
};

/**
 * 4. Contact form — internal admin notification
 */
export const contactAdminAlert = (p = {}) => {
  const subject = `[Contact] ${p.topic || 'general'} · ${p.name || 'anon'} · ${p.company || ''}`;
  const rows = [
    ['Name',    p.name || '—'],
    ['Email',   p.email || '—'],
    ['Company', p.company || '—'],
    ['Topic',   p.topic || '—'],
    ['Message', p.message || '—'],
  ];
  const html = shell({
    preheader: `Internal: contact message from ${p.email || 'unknown'}.`,
    body:
      overline('Internal alert · contact') +
      display(p.topic || 'New contact message') +
      kvBlock(rows),
  });
  const text = `${txtHeader(subject)}\n${txtKV(rows)}${txtFooter}`;
  return { subject, html, text };
};

/**
 * 5. Supplier application — applicant acknowledgement
 */
export const supplierApplicationConfirmation = (p = {}) => {
  const subject = `Application received — GIWAHS supplier listing`;
  const html = shell({
    preheader: `Your application is in the verification queue.`,
    body:
      overline('Supplier application') +
      display('Application received.') +
      para(`Thanks${p.contact_name ? ', ' + p.contact_name : ''}. Your application to list ${p.factory_name ? `${p.factory_name} ` : ''}on GIWAHS has reached our verification desk. We will review your factory's certifications, brand associations and capacity claims before publishing a profile.`) +
      kvBlock([
        ['Factory',      p.factory_name || '—'],
        ['Country',      p.country || '—'],
        ['Categories',   (p.categories || []).join(', ') || '—'],
        ['Capacity',     p.capacity || '—'],
        ['Interest',     p.interest_level || '—'],
      ]) +
      para(`Verification typically takes 3 to 7 business days. We will contact you on the email you provided with any document requests. Profiles that pass verification appear in the public directory with a Verified badge.`) +
      cta(`${PLATFORM}/trust-score`, 'Read about Trust Score'),
  });
  const text = `${txtHeader(subject)}
Thanks${p.contact_name ? ', ' + p.contact_name : ''}.

Your application to list ${p.factory_name ? p.factory_name + ' ' : ''}on
GIWAHS has reached our verification desk. We will review your factory's
certifications, brand associations and capacity claims before publishing
a profile.

  Factory:     ${p.factory_name || '—'}
  Country:     ${p.country || '—'}
  Categories:  ${(p.categories || []).join(', ') || '—'}
  Capacity:    ${p.capacity || '—'}
  Interest:    ${p.interest_level || '—'}

Verification typically takes 3 to 7 business days. We will contact you
on the email you provided with any document requests. Profiles that
pass verification appear in the public directory with a Verified badge.

Read about Trust Score: ${PLATFORM}/trust-score
${txtFooter}`;
  return { subject, html, text };
};

/**
 * 6. Supplier application — internal admin notification
 */
export const supplierApplicationAdminAlert = (p = {}) => {
  const subject = `[Supplier App] ${p.factory_name || 'Unnamed'} · ${p.country || ''} · ${(p.categories || []).join('+')}`;
  const rows = [
    ['Factory',       p.factory_name || '—'],
    ['Country',       p.country || '—'],
    ['City',          p.city || '—'],
    ['Website',       p.website || '—'],
    ['Categories',    (p.categories || []).join(', ') || '—'],
    ['Capacity',      p.capacity || '—'],
    ['MOQ range',     p.moq || '—'],
    ['Brand assoc.',  p.brand_associations || '—'],
    ['Certifications',p.certifications || '—'],
    ['Contact',       p.contact_name || '—'],
    ['Email',         p.contact_email || '—'],
    ['Phone',         p.contact_phone || '—'],
    ['Interest',      p.interest_level || '—'],
    ['Notes',         p.notes || '—'],
  ];
  const html = shell({
    preheader: `Internal: supplier application from ${p.contact_email || 'unknown'}.`,
    body:
      overline('Internal alert · supplier application') +
      display(p.factory_name || 'New supplier application') +
      kvBlock(rows),
  });
  const text = `${txtHeader(subject)}\n${txtKV(rows)}${txtFooter}`;
  return { subject, html, text };
};

/**
 * 7. Account signup — welcome email
 */
export const signupWelcome = (p = {}) => {
  const subject = `Welcome to GIWAHS — your buyer account is active`;
  const html = shell({
    preheader: `Your saved factories and compare list are now yours alone.`,
    body:
      overline('Buyer account') +
      display('Welcome to GIWAHS.') +
      para(`Your buyer account is active. Save factories, compare suppliers side-by-side, and your RFQ history is now tied to your account — accessible from any device you sign into.`) +
      kvBlock([
        ['Email',      p.email || '—'],
        ['Account',    'Buyer'],
        ['Created',    p.created_at || new Date().toISOString().slice(0, 10)],
      ]) +
      para(`What to do next:`) +
      `<tr><td style="padding:0 32px 24px 32px;font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;font-size:14px;line-height:1.7;color:${COLOR_FG};">
        — Browse the <a href="${PLATFORM}/factories" style="color:${COLOR_FG};">factory directory</a> and bookmark factories that fit.<br>
        — Add 2–4 factories to <a href="${PLATFORM}/compare" style="color:${COLOR_FG};">compare</a> for side-by-side analysis.<br>
        — File an <a href="${PLATFORM}/rfq" style="color:${COLOR_FG};">RFQ</a> when you're ready — pre-fills with your account details.
      </td></tr>` +
      cta(`${PLATFORM}/factories`, 'Browse factories'),
  });
  const text = `${txtHeader(subject)}
Your buyer account is active. Save factories, compare suppliers
side-by-side, and your RFQ history is now tied to your account.

  Email:    ${p.email || '—'}
  Account:  Buyer
  Created:  ${p.created_at || new Date().toISOString().slice(0, 10)}

What to do next:
  — Browse the factory directory at ${PLATFORM}/factories and bookmark
    factories that fit.
  — Add 2–4 factories to compare for side-by-side analysis.
  — File an RFQ when you're ready — pre-fills with your account details.
${txtFooter}`;
  return { subject, html, text };
};

/**
 * 8. RFQ status update — buyer notification when admin advances RFQ
 *    state via /admin/rfqs. Reuses the QR + tracking card.
 */
export const rfqStatusUpdate = (p = {}) => {
  const status = p.status_label || 'Update';
  const subject = `RFQ ${p.reference_code || ''} — status: ${status}`;
  const tracking_url = p.tracking_url || `${PLATFORM}/track?ref=${encodeURIComponent(p.reference_code || '')}`;
  const supportEmail = p.support_email || SUPPORT_EMAIL;
  const rows = [
    ['Reference',       p.reference_code || '—'],
    ['New status',      status],
    ['Product',         p.product_name || p.category || '—'],
    ['Quantity',        p.quantity ? `${p.quantity} units` : '—'],
    ['Note',            p.note || '—'],
    ['Next expected',   p.next_eta || 'shortly'],
  ];
  const html = shell({
    preheader: `RFQ ${p.reference_code || ''} moved to ${status}.`,
    body:
      overline('RFQ status update') +
      display(`Status: ${status}.`) +
      statusPill(status) +
      para(`Hi${p.contact_name ? ', ' + p.contact_name : ''}. Your RFQ ${p.reference_code || ''} just moved to ${status}. ${p.note || ''}`.trim()) +
      qrCard({ tracking_url, qr_data_url: p.qr_data_url, reference_code: p.reference_code }) +
      kvBlock(rows) +
      para(`Track the full timeline anytime via the link above. Reply to this email or write to ${supportEmail} with questions.`) +
      cta(tracking_url, 'Open tracking page'),
  });
  const text = `${txtHeader(subject)}
Hi${p.contact_name ? ', ' + p.contact_name : ''}.

Your RFQ ${p.reference_code || ''} just moved to ${status}.
${p.note || ''}

Track this RFQ: ${tracking_url}

${txtKV(rows)}

Questions?  Reply to this email or write to ${supportEmail}.
${txtFooter}`;
  return { subject, html, text };
};

/**
 * 9. Supplier claim received — internal alert when a supplier claims a listing.
 */
export const supplierClaimReceived = (p = {}) => {
  const subject = `[Supplier Claim] ${p.supplier_slug || 'listing'} · ${p.contact_email || ''}`;
  const rows = [
    ['Supplier slug',   p.supplier_slug || '—'],
    ['Contact email',   p.contact_email || '—'],
    ['Role',            p.contact_role || '—'],
    ['Proof URL',       p.proof_url || '—'],
    ['Reason / notes',  p.reason || '—'],
    ['Submitted',       p.submitted_at || new Date().toISOString().slice(0, 19).replace('T', ' ')],
  ];
  const html = shell({
    preheader: `Internal: claim submitted for ${p.supplier_slug || 'a listing'}.`,
    body:
      overline('Internal alert · supplier claim') +
      display('New listing claim.') +
      kvBlock(rows) +
      para('Review the claim, verify proof, then approve or reject from the admin claims queue.') +
      cta(`${PLATFORM}/admin/claims`, 'Open claims queue'),
  });
  const text = `${txtHeader(subject)}
${txtKV(rows)}
Review: ${PLATFORM}/admin/claims
${txtFooter}`;
  return { subject, html, text };
};

/**
 * 10. Supplier RFQ assigned — fires when admin attaches a supplier to an RFQ.
 *     Recipient = the supplier's contact email.
 */
export const supplierRfqAssigned = (p = {}) => {
  const subject = `New RFQ in your inbox — ${p.reference_code || ''}`;
  const supplier_url = `${PLATFORM}/supplier/rfq/${encodeURIComponent(p.reference_code || '')}`;
  const supportEmail = p.support_email || SUPPORT_EMAIL;
  const rows = [
    ['Reference',       p.reference_code || '—'],
    ['Category',        p.category || '—'],
    ['Product',         p.product_name || '—'],
    ['Quantity',        p.quantity ? `${p.quantity} units` : '—'],
    ['Destination',     p.destination_country || '—'],
    ['Target price',    p.target_price || '—'],
    ['Timeline',        p.timeline || '—'],
    ['Quote due',       p.quote_due || 'within 3 business days'],
  ];
  const html = shell({
    preheader: `GIWAHS sourcing desk has attached your factory to RFQ ${p.reference_code || ''}.`,
    body:
      overline('Supplier inbox · new RFQ') +
      display('You have a new RFQ.') +
      para(`Hi${p.supplier_name ? ', ' + p.supplier_name : ''}. The GIWAHS sourcing desk has attached your factory to a buyer RFQ. Open the brief, then submit your quote — fastest credible quote typically wins the buyer review.`) +
      kvBlock(rows) +
      para(`Submit your quote directly via the supplier inbox. Questions? Reply to this email or write to ${supportEmail}.`) +
      cta(supplier_url, 'Open RFQ and submit quote'),
  });
  const text = `${txtHeader(subject)}
Hi${p.supplier_name ? ', ' + p.supplier_name : ''}.

GIWAHS has attached your factory to a buyer RFQ.
Open and quote: ${supplier_url}

${txtKV(rows)}

Questions?  Reply to this email or write to ${supportEmail}.
${txtFooter}`;
  return { subject, html, text };
};

/**
 * 11. Quote submitted — buyer notification.
 */
export const quoteSubmittedBuyer = (p = {}) => {
  const subject = `New quote on RFQ ${p.reference_code || ''}`;
  const tracking_url = p.tracking_url || `${PLATFORM}/track?ref=${encodeURIComponent(p.reference_code || '')}`;
  const rows = [
    ['Reference',       p.reference_code || '—'],
    ['Factory',         p.supplier_name || p.supplier_slug || '—'],
    ['Unit price',      p.unit_price || '—'],
    ['MOQ',             p.moq || '—'],
    ['Lead time',       p.lead_time_days ? `${p.lead_time_days} days` : '—'],
    ['Notes',           (p.notes || '').slice(0, 280) || '—'],
  ];
  const html = shell({
    preheader: `A factory just quoted your RFQ ${p.reference_code || ''}.`,
    body:
      overline('RFQ · new quote received') +
      display('You have a new quote.') +
      para(`Hi${p.contact_name ? ', ' + p.contact_name : ''}. A factory just submitted a quote on your RFQ. Open the tracking page to review the full quote and compare against any others.`) +
      qrCard({ tracking_url, qr_data_url: p.qr_data_url, reference_code: p.reference_code }) +
      kvBlock(rows) +
      cta(tracking_url, 'Review quote'),
  });
  const text = `${txtHeader(subject)}
A factory just quoted your RFQ ${p.reference_code || ''}.

${txtKV(rows)}

Review the full quote: ${tracking_url}
${txtFooter}`;
  return { subject, html, text };
};

/**
 * 12. Quote submitted — internal admin alert.
 */
export const quoteSubmittedAdminAlert = (p = {}) => {
  const subject = `[Quote] ${p.reference_code || ''} · ${p.supplier_slug || ''} · ${p.unit_price || ''}`;
  const rows = [
    ['Reference',       p.reference_code || '—'],
    ['Supplier',        p.supplier_name || p.supplier_slug || '—'],
    ['Supplier slug',   p.supplier_slug || '—'],
    ['Unit price',      p.unit_price || '—'],
    ['MOQ',             p.moq || '—'],
    ['Lead time (days)',p.lead_time_days || '—'],
    ['Attachments',     p.attachments_url || '—'],
    ['Notes',           p.notes || '—'],
    ['Submitted',       p.submitted_at || new Date().toISOString().slice(0, 19).replace('T', ' ')],
  ];
  const html = shell({
    preheader: `Internal: ${p.supplier_slug || 'a supplier'} quoted ${p.reference_code || ''}.`,
    body:
      overline('Internal alert · new quote') +
      display(p.reference_code || 'New quote received') +
      kvBlock(rows) +
      cta(`${PLATFORM}/admin/rfqs/${encodeURIComponent(p.reference_code || '')}`, 'Open in admin'),
  });
  const text = `${txtHeader(subject)}
${txtKV(rows)}
Open in admin: ${PLATFORM}/admin/rfqs/${p.reference_code || ''}
${txtFooter}`;
  return { subject, html, text };
};

/**
 * 13. Quote accepted — supplier notification.
 *     Fires when the buyer accepts THIS supplier's quote.
 */
export const quoteAccepted = (p = {}) => {
  const subject = `Your quote was accepted — RFQ ${p.reference_code || ''}`;
  const supplier_url = `${PLATFORM}/supplier/rfq/${encodeURIComponent(p.reference_code || '')}`;
  const supportEmail = p.support_email || SUPPORT_EMAIL;
  const rows = [
    ['Reference',       p.reference_code || '—'],
    ['Buyer',           p.buyer_name || '—'],
    ['Buyer company',   p.buyer_company || '—'],
    ['Unit price',      p.unit_price || '—'],
    ['MOQ',             p.moq || '—'],
    ['Lead time',       p.lead_time_days ? `${p.lead_time_days} days` : '—'],
    ['Buyer note',      (p.buyer_note || '').slice(0, 280) || '—'],
  ];
  const html = shell({
    preheader: `The buyer accepted your quote on RFQ ${p.reference_code || ''}.`,
    body:
      overline('Quote · accepted') +
      display('Your quote was accepted.') +
      para(`Hi${p.supplier_name ? ', ' + p.supplier_name : ''}. The buyer accepted your quote on RFQ ${p.reference_code || ''}. The GIWAHS sourcing desk will introduce you to the buyer to finalise contract, samples, and shipping logistics.`) +
      kvBlock(rows) +
      para(`Next steps land via the sourcing desk within 1 business day. Questions? Reply to this email or write to ${supportEmail}.`) +
      cta(supplier_url, 'Open the RFQ'),
  });
  const text = `${txtHeader(subject)}
Your quote was accepted on RFQ ${p.reference_code || ''}.

${txtKV(rows)}

Open the RFQ: ${supplier_url}
Questions? ${supportEmail}
${txtFooter}`;
  return { subject, html, text };
};

/**
 * 14. Quote rejected — supplier notification.
 *     Fires when the buyer rejects a specific quote (manual reject, OR
 *     auto-reject because another supplier's quote was accepted).
 */
export const quoteRejected = (p = {}) => {
  const subject = `Quote update — RFQ ${p.reference_code || ''}`;
  const rows = [
    ['Reference',       p.reference_code || '—'],
    ['Unit price',      p.unit_price || '—'],
    ['MOQ',             p.moq || '—'],
    ['Lead time',       p.lead_time_days ? `${p.lead_time_days} days` : '—'],
    ['Reason',          (p.buyer_note || '').slice(0, 280) || (p.auto_rejected ? 'Another quote was accepted by the buyer.' : '—')],
  ];
  const html = shell({
    preheader: `The buyer passed on your quote for RFQ ${p.reference_code || ''}.`,
    body:
      overline('Quote · not selected') +
      display('Quote not selected.') +
      para(`Hi${p.supplier_name ? ', ' + p.supplier_name : ''}. The buyer has passed on your quote for RFQ ${p.reference_code || ''}. ${p.auto_rejected ? 'Another supplier’s quote was accepted on this brief.' : 'They cited the reason below where provided.'} New RFQs in your category continue to land in your inbox — keep an eye on it.`) +
      kvBlock(rows) +
      cta(`${PLATFORM}/supplier/dashboard`, 'Open inbox'),
  });
  const text = `${txtHeader(subject)}
Your quote on ${p.reference_code || ''} was not selected.
${txtKV(rows)}
${txtFooter}`;
  return { subject, html, text };
};

// ─── Registry ─────────────────────────────────────────────────────────
export const verificationRejected = (p = {}) => ({
  subject: `Verification update — please re-submit`,
  html: shell({ preheader: 'Verification cannot proceed.', body:
    overline('Verification · update') + display('Verification update.') +
    para(`Our team reviewed your verification request and could not approve it as-is.${p.note ? ' Reviewer note: ' + p.note : ''} You can update your documents and re-submit at any time.`) +
    cta(`${PLATFORM}/supplier/verification`, 'Open verification') }),
  text: `[GIWAHS]\nVerification needs an update. ${p.note ? 'Note: ' + p.note : ''}\n${PLATFORM}/supplier/verification`,
});

export const verificationRevoked = (p = {}) => ({
  subject: `Verification status changed`,
  html: shell({ preheader: 'Your verification status has been changed.', body:
    overline('Verification · revoked') + display('Verification status changed.') +
    para(`Your verified status was revoked by the GIWAHS team.${p.note ? ' Reason: ' + p.note : ''} Reply to this email if you believe this is in error.`) }),
  text: `[GIWAHS]\nVerification revoked. ${p.note ? 'Reason: ' + p.note : ''}`,
});

/**
 * Verification submitted — SUPPLIER acknowledgement that the request was filed
 * (sent right after they click Submit, before/regardless of payment).
 */
export const verificationSubmittedSupplier = (p = {}) => {
  const tier = String(p.requested_tier || '').toUpperCase();
  const subject = `Verification request received — ${tier || 'pending review'}`;
  const preheader = 'We have your verification request. Next step depends on your tier.';
  const requiresPayment = ['GOLD', 'ELITE'].includes(tier);
  const rows = [
    ['Supplier', p.supplier_name || p.supplier_slug || '—'],
    ['Requested tier', tier || '—'],
    ['Submitted at', (p.submitted_at || new Date().toISOString()).slice(0, 19).replace('T', ' ') + ' UTC'],
    ['Documents on file', String(p.doc_count ?? '—')],
  ];
  return {
    subject,
    html: shell({ preheader, body:
      overline('Verification · received') +
      display('We have your verification request.') +
      para(`Thanks for submitting your ${tier || 'verification'} application. We've logged the request and it is now in our queue.`) +
      kvBlock(rows) +
      para(requiresPayment
        ? `Your ${tier} tier requires a one-time annual fee. Once payment is received, your application moves into formal review. You will get a separate confirmation email the moment payment clears.`
        : 'A GIWAHS reviewer will assess your documents within 2 business days. We will email you the outcome.') +
      cta(`${PLATFORM}/supplier/verification`, requiresPayment ? 'Complete payment' : 'Open verification') }),
    text: `${txtHeader(subject)}\nWe have your ${tier || 'verification'} request.\n\n${txtKV(rows)}\n\n${requiresPayment
      ? `${tier} tier requires a one-time annual fee. Once payment is received, your application moves into formal review.`
      : 'A GIWAHS reviewer will assess your documents within 2 business days.'}\n\nManage: ${PLATFORM}/supplier/verification${txtFooter}`,
  };
};

/**
 * Payment received — sent the moment Stripe confirms a Gold/Elite payment.
 * Triggered by the backend Stripe webhook (checkout.session.completed) and
 * the return-poll status endpoint, both routing through mark_verification_paid.
 */
export const paymentReceived = (p = {}) => {
  const tier = String(p.tier || '').toUpperCase();
  const amount = typeof p.amount === 'number' ? p.amount.toFixed(2) : (p.amount || '');
  const currency = String(p.currency || 'NZD').toUpperCase();
  const subject = 'Your GIWAHS Verification is Now Under Review';
  const preheader = `Payment received — ${tier} verification application is in review.`;
  const rows = [
    ['Supplier', p.supplier_name || p.supplier_slug || '—'],
    ['Tier requested', tier || '—'],
    ['Amount', amount ? `${currency} $${amount}` : '—'],
    ['Receipt', p.session_id ? `Stripe ${p.session_id.slice(0, 12)}…` : '—'],
    ['Paid at', (p.paid_at || new Date().toISOString()).slice(0, 19).replace('T', ' ') + ' UTC'],
  ];
  return {
    subject,
    html: shell({ preheader, body:
      overline('Verification · payment received') +
      display('Your verification is now under review.') +
      para(`Thanks — we've received your ${tier} verification payment. Your application has been moved into our active review queue. A GIWAHS reviewer will assess your documents within 2 business days and notify you of the outcome by email.`) +
      kvBlock(rows) +
      para('Stripe will send a separate official receipt for your records. You do not need to do anything further until you hear from us.') +
      cta(`${PLATFORM}/supplier/verification`, 'View application status') }),
    text: `${txtHeader(subject)}\nThanks — we've received your ${tier} verification payment.\nYour application is in our active review queue.\n\n${txtKV(rows)}\n\nA reviewer will respond within 2 business days. Stripe will send your official receipt separately.\n\nStatus: ${PLATFORM}/supplier/verification${txtFooter}`,
  };
};

export const TEMPLATES = {
  rfq_confirmation:                rfqConfirmation,
  rfq_admin_alert:                 rfqAdminAlert,
  contact_ack:                     contactAck,
  contact_admin_alert:             contactAdminAlert,
  supplier_application_confirmation: supplierApplicationConfirmation,
  supplier_application_admin_alert:  supplierApplicationAdminAlert,
  signup_welcome:                  signupWelcome,
  // Network-effect loop emails
  rfq_status_update:               rfqStatusUpdate,
  supplier_claim_received:         supplierClaimReceived,
  supplier_rfq_assigned:           supplierRfqAssigned,
  quote_submitted_buyer:           quoteSubmittedBuyer,
  quote_submitted_admin_alert:     quoteSubmittedAdminAlert,
  // Transaction-closing emails
  quote_accepted:                  quoteAccepted,
  quote_rejected:                  quoteRejected,
  // Aliases for the operator-spec naming (so the dispatcher can be called with either)
  rfq_submitted:                   rfqConfirmation,      // alias of rfq_confirmation (buyer-facing)
  supplier_quote_received:         quoteSubmittedBuyer,  // alias of quote_submitted_buyer (buyer-facing)
  // Verification (P4) — all kept brief; reuse helpers from this file
  verification_submitted:          verificationSubmittedSupplier,  // supplier-facing (NEW)
  verification_submitted_admin:    (p = {}) => ({                  // existing internal alert (renamed)
    subject: `[Verification] ${p.supplier_slug || ''} requested ${(p.requested_tier || '').toUpperCase()}`,
    html: shell({ preheader: 'New verification request awaiting review.', body:
      overline('Internal · verification') + display('New verification request') +
      kvBlock([['Supplier slug', p.supplier_slug || '—'], ['Requested tier', p.requested_tier || '—'], ['Attestation', (p.attestation || '').slice(0, 280) || '—']]) +
      cta(`${PLATFORM}/admin/verifications`, 'Open admin queue') }),
    text: `[GIWAHS internal]\n${p.supplier_slug || ''} requested ${(p.requested_tier || '').toUpperCase()}.\nOpen: ${PLATFORM}/admin/verifications`,
  }),
  payment_received:                paymentReceived,
  verification_approved:           (p = {}) => ({
    subject: `You're now GIWAHS ${(p.final_tier || '').toUpperCase()}`,
    html: shell({ preheader: `Your factory is now ${(p.final_tier || '').toUpperCase()} on GIWAHS.`, body:
      overline('Verification · approved') + display(`You're now GIWAHS ${(p.final_tier || '').toUpperCase()}.`) +
      para(`Your tier badge will appear on your factory profile, every quote you submit, and across the GIWAHS directory immediately. Verification is valid until ${(p.expires_at || '').slice(0, 10) || '—'}.`) +
      cta(`${PLATFORM}/supplier/verification`, 'Open verification') }),
    text: `[GIWAHS]\nYou're now ${(p.final_tier || '').toUpperCase()}. Valid until ${(p.expires_at || '').slice(0, 10)}.`,
  }),
  verification_rejected:           verificationRejected,
  verification_revoked:            verificationRevoked,
};

export const renderTemplate = (kind, payload) => {
  const fn = TEMPLATES[kind];
  if (!fn) throw new Error(`Unknown email template: ${kind}`);
  return fn(payload || {});
};
